import React, { useState, useEffect } from 'react'
import { Row, Col, Form, Alert } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { cloneDeep } from 'lodash'
import styled from 'styled-components'
import { MdLibraryAdd } from 'react-icons/md'
import useStores from '../../stores/useStore'
import { useAppointmentsAdministrationState } from '../../stores/Administration/AppointmentsAdministrationProvider'
import CabinSelect from './CabinSelect'
import DateSelect from './DateSelect'
import DailyAppointmentsOverview from './DailyAppointmentsOverview'
import CreateNewReportOverlay from '../Files/CreateNewReportOverlay'
import CreateRightOverlay from './CreateRightOverlay'
import ModalComponent from '../Modals/ModalComponent'
import DailyAppointmentsCompareModal from './DailyAppointmentsCompareModal'
import { StyledDisabledContainer } from '../Forms/StyledDisabledContainer'
import { ContentArea } from '../Page/logix2020/ContentArea'
import { hoursArray } from '../../utils/hoursAndMinutes'
import { colors } from '../../styles/colors'
import { useObserver } from 'mobx-react'
import { t } from 'i18next'

const StyledContentArea = styled(ContentArea)`
padding-left: 55.47px;
@media screen and (min-width: 992px) {
    max-width: calc(100% - 480px);
    min-width: 600px;
  }
  hr {
    background-color: ${colors.borderColor};
    margin: 35px 0;
  }
}
@media (max-width: 1081px) {
  padding-left: 15px;
}
@media (max-width: 576px) {
    h1 {
        font-size: 1rem;
        margin-bottom: 10px;
    }
    hr {
        margin: 15px;
    }
}
`
const MobileFullWidthButton = styled(Link)`
  @media (max-width: 576px) {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: center;
  }
`
const InlineIcon = styled(MdLibraryAdd)`
  margin-right: 10px;
  margin-bottom: 2px;
`

const actionModalTexts = {
  deleteLocks: {
    modalTitle: t('appointment:deleteLocks'),
    buttonText: t('unlock'),
    bodyText: t('appointment:areYouSureDeleteLocks'),
  },
  deleteAppointments: {
    modalTitle: t('appointment:deleteAppointments'),
    buttonText: t('delete'),
    bodyText: t('appointment:areYouSureDeleteAppointments'),
  },
  lockAll: {
    modalTitle: t('appointment:lockAll'),
    buttonText: t('lock'),
    bodyText: t('appointment:areYouSureLockAll'),
  },
  deleteGroupReservations: {
    modalTitle: t('appointment:deleteGroupReservations'),
    buttonText: t('delete'),
    bodyText: t('appointment:areYouSureDeleteGroupReservations'),
  },
}

const AppointmentsContentArea = () => {
  const [selectedAction, setSelectedAction] = useState('')
  const [selectedDateHoursArray, setSelectedDateHoursArray] = useState(
    cloneDeep(hoursArray)
  )
  const [modalVariant, setModalVariant] = useState('')
  const { profileStore, overlayRightStore } = useStores()
  const { profile } = profileStore
  const {
    selectedCabin,
    selectedDays,
    lockAll,
    deleteEvent,
    isStep1Done,
    isStep2Done,
    error,
    bookingSlots,
    selectedDate,
    showDailyAppointmentsCompareModal,
    successMessage,
    setErrorMessage,
    setSuccessMessage,
    blockedDates,
  } = useAppointmentsAdministrationState()
  const showOverviewOverlay = useObserver(
    () => overlayRightStore.openOverlay === 'dailyAppointmentsOverview'
  )
  const showCreateReportOverlay = useObserver(
    () => overlayRightStore.openOverlay === 'appointmentAdminReport'
  )

  useEffect(() => {
    if (bookingSlots.length) {
      let filteredBookingSlots = bookingSlots.filter(
        (slot) =>
          slot.startTime.includes(selectedDate) &&
          (slot.status === 'isAppointment' || slot.isLocked)
      )
      let hoursArrayCopy = cloneDeep(hoursArray)
      filteredBookingSlots.forEach((slot) => {
        let hour = parseInt(slot.startTime.substring(11, 13))
        hoursArrayCopy[hour].push(slot)
      })
      setSelectedDateHoursArray(hoursArrayCopy)
    }
  }, [selectedDate, bookingSlots, selectedDays])

  const closeAllOverlays = () => {
    setSelectedAction('')
    overlayRightStore.closeAll()
  }

  const selectAction = (action) => {
    setSelectedAction(action)
    setModalVariant(action.includes('delete') ? 'alert-danger' : 'alert-info')

    // Clear any previous messages
    setErrorMessage('')
    setSuccessMessage('')
    switch (action) {
      case 'report':
        overlayRightStore.openOrClose('appointmentAdminReport')
        break
      case 'deleteGroupReservations':
      case 'deleteAppointments':
      case 'deleteLocks':
        setModalVariant('alert-danger')
        break
      default:
        setModalVariant('alert-info')
    }
  }

  const modalFunction = async (type) => {
    try {
      switch (type) {
        case 'lockAll':
          await lockAll()
          setSuccessMessage(t('appointment:lockSuccess'))
          break
        case 'deleteLocks':
          await deleteEvent(type)
          setSuccessMessage(t('appointment:unlockSuccess'))
          break
        case 'deleteAppointments':
          await deleteEvent(type)
          setSuccessMessage(t('appointment:deleteSuccess'))
          break
        case 'deleteGroupReservations':
          await deleteEvent(type)
          setSuccessMessage(t('appointment:deleteGroupSuccess'))
          break
        default:
          break
      }
      setSelectedAction('')
    } catch (error) {
      setSelectedAction('')
    }
  }

  return (
    <StyledContentArea>
      <h1>{t('appointment:step')} 1</h1>
      <Row>
        <Col lg='5'>
          <CabinSelect />
        </Col>
      </Row>
      <hr></hr>
      <StyledDisabledContainer disabled={!isStep1Done}>
        <Row>
          <Col>
            <h1>{t('appointment:step')} 2</h1>
            <DateSelect blockedDates={blockedDates} />

            <MobileFullWidthButton
              className='mb-3 btn btn-outline-secondary'
              to={`/appointment/${profile.id}`}
            >
              <InlineIcon />
              {t('appointment:newAppointment')}
            </MobileFullWidthButton>
          </Col>
          {selectedDays.length > 0 && (
            <Col>
              <DailyAppointmentsOverview
                date={selectedDate}
                selectedDateHoursArray={selectedDateHoursArray}
                setSelectedAction={setSelectedAction}
              />
            </Col>
          )}
        </Row>
      </StyledDisabledContainer>
      <hr></hr>
      <StyledDisabledContainer disabled={!isStep2Done}>
        <h1>{t('appointment:step')} 3</h1>
        <Row>
          <Col lg='5'>
            <Form.Group>
              <Form.Label>{t('selectAction')}</Form.Label>
              <Form.Select
                onChange={(e) => {
                  selectAction(e.target.value)
                }}
                value={selectedAction}
              >
                <option disabled hidden value=''>
                  {t('pleaseSelect')}
                </option>
                <option value='report'>
                  {t('reports:createCabineReport')}
                </option>
                <option value='lockAll'>{t('appointment:lockAll')}</option>
                <option value='deleteLocks'>
                  {t('appointment:deleteLocks')}
                </option>
                <option value='deleteAppointments'>
                  {t('appointment:deleteAppointments')}
                </option>
                <option value='deleteGroupReservations'>
                  {t('appointment:deleteGroupReservations')}
                </option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </StyledDisabledContainer>
      <CreateRightOverlay
        visible={showOverviewOverlay}
        onClose={closeAllOverlays}
      />
      <CreateNewReportOverlay
        visible={showCreateReportOverlay}
        onClose={closeAllOverlays}
        reportTypeId={'cabinEventStatistic'}
        preSelectedData={{
          dateRange: [
            selectedDays.length ? new Date(selectedDays[0][0]) : null,
            selectedDays.length
              ? new Date(selectedDays[selectedDays.length - 1][0])
              : null,
          ],
          cabinID: selectedCabin,
        }}
      />
      {selectedAction in actionModalTexts && (
        <ModalComponent
          ModalTitle={actionModalTexts[selectedAction].modalTitle}
          functionButton1={() => modalFunction(selectedAction)}
          textButton1={actionModalTexts[selectedAction].buttonText}
          variantButton1='danger'
          variantBody={modalVariant}
          BodyPart1={actionModalTexts[selectedAction].bodyText}
          functionCancel={() => {
            setSelectedAction('')
          }}
        />
      )}
      <Col lg='5' className='mt-2'>
        {error && <Alert variant='danger'>{error}</Alert>}
        {successMessage && <Alert variant='success'>{successMessage}</Alert>}
      </Col>

      {showDailyAppointmentsCompareModal && <DailyAppointmentsCompareModal />}
    </StyledContentArea>
  )
}
export default AppointmentsContentArea
